// ============================================================================
// CANCUN100 — armarDescriptivo.js  (Bloque 09: arma el `data` de los PDF descriptivos)
// ----------------------------------------------------------------------------
// Funciones PURAS (sin Supabase ni React): reciben la fila de `descriptivos_contenido`
// (o el renglón de un temporal), las opciones del panel y la marca, y devuelven el
// `data` que esperan PDFPremium2 / PDFDescriptivo.
// Los toggles se aplican VACIANDO la sección (el componente no dibuja lo vacío).
// NUNCA entran netos, sobreprecios ni comisiones: el precio llega ya resuelto
// (público Sala o precio Top Tours) desde el panel.
// ============================================================================

export const IDIOMAS = [['es', 'Español'], ['en', 'English'], ['pt', 'Português']];

export const SECCIONES = [
  ['descripcion', 'Descripción'],
  ['highlights', 'Lo más destacado'],
  ['itinerario', 'Itinerario'],
  ['edades', 'Edades'],
  ['dias', 'Días'],
  ['punto', 'Punto de encuentro'],
  ['antes', 'Antes de ir (notas y restricciones)'],
  ['galeria', 'Galería de fotos'],
  ['faq', 'Preguntas frecuentes'],
  ['confianza', 'Por qué reservar con nosotros'],
];
export const TOGGLES_COMPLETO = Object.fromEntries(SECCIONES.map(([k]) => [k, true]));
// Resumido: portada, descripción corta, lo más destacado, incluye/no incluye, días y edades.
export const TOGGLES_RESUMIDO = {
  descripcion: true, highlights: true, itinerario: false, edades: true, dias: true,
  punto: false, antes: false, galeria: false, faq: false, confianza: false,
};

const CANCELACION = /cancel|reembols|refund|no[\s-]?show|cargo del 50/i;
const arr = (v) => (Array.isArray(v) ? v : v == null || v === '' ? [] : [v]);
const lineas = (v) => (Array.isArray(v) ? v : String(v || '').split(/\r?\n|;|•/))
  .map((x) => String(x || '').trim()).filter(Boolean);
const sinCancel = (l) => l.filter((x) => !CANCELACION.test(typeof x === 'string' ? x : JSON.stringify(x)));
const tieneContenido = (c) => !!c && typeof c === 'object' && Object.keys(c).some((k) => {
  const v = c[k];
  return Array.isArray(v) ? v.length > 0 : v && typeof v === 'object' ? Object.keys(v).length > 0 : !!String(v || '').trim();
});

// Idiomas con contenido (default es). Sin ficha → solo español (el Básico es en español).
export const idiomasDisponibles = (fila) => {
  if (!fila || !fila.tiene_ficha) return ['es'];
  const l = IDIOMAS.map(([k]) => k).filter((k) => tieneContenido(fila['contenido_' + k]));
  return l.length ? l : ['es'];
};

const ETQ_EDADES = { es: ['Adultos', 'Menores', 'Infantes'], en: ['Adults', 'Children', 'Infants'], pt: ['Adultos', 'Crianças', 'Bebês'] };
const num = (v) => (v == null || v === '' || isNaN(Number(v)) ? null : Number(v));
// Solo rangos con números (sin números → la tarjeta de edades no se dibuja).
export const edadesDe = (fila, idioma = 'es') => {
  if (!fila) return [];
  const [ad, me, inf] = ETQ_EDADES[idioma] || ETQ_EDADES.es;
  const aMin = num(fila.edad_adulto_min), mMin = num(fila.edad_menor_min), mMax = num(fila.edad_menor_max), iMax = num(fila.edad_infante_max);
  return [
    [ad, aMin != null ? `${aMin}+` : null],
    [me, mMin != null && mMax != null ? `${mMin}-${mMax}` : mMax != null ? `≤${mMax}` : null],
    [inf, iMax != null ? `0-${iMax}` : null],
  ].filter(([, v]) => v).map(([etiqueta, valor]) => ({ etiqueta, valor }));
};

const duracionDe = (fila, ig) => {
  const d = ig && String(ig.duracion || '').trim();
  if (d) return d;
  const h = num(fila && fila.duracion_horas);
  return h ? `${h} h` : '';
};

export const fotosDe = (fila, conGaleria = true) => {
  if (!fila) return [];
  const out = [];
  if (fila.portada_url) out.push({ orden: 0, url: fila.portada_url });
  if (conGaleria) arr(fila.galeria_urls).filter(Boolean).slice(0, 4).forEach((u, i) => out.push({ orden: i + 1, url: u }));
  return out;
};

// Precio para el PDF. `p` = { ad, men, moneda } (lo que quedó en el panel: sugerido o editado).
// `tasa` = MXN por USD. `leyendas` = { promo, porPersona, apartado } ya traducidas (o null cada una).
export const precioPdf = (p, tasa, leyendas = {}) => {
  if (!p || !(Number(p.ad) > 0)) return { mostrar: false };
  const usd = p.moneda === 'USD';
  const t = Number(tasa) > 0 ? Number(tasa) : null;
  return {
    mostrar: true,
    monto: Number(p.ad),
    moneda: usd ? 'USD' : 'MXN',
    montoMenor: Number(p.men) > 0 ? Number(p.men) : null,
    montoMxn: usd && t ? Math.round(Number(p.ad) * t) : null,
    montoMenorMxn: usd && t && Number(p.men) > 0 ? Math.round(Number(p.men) * t) : null,
    promo: leyendas.promo || null,
    porPersona: leyendas.porPersona || null,
    apartado: leyendas.apartado || null,
  };
};

// Protección de neto (Bloque 09 v3, aplica a TODOS). Compara en memoria y devuelve solo un mensaje:
// el neto NUNCA se muestra, imprime ni manda a consola.
//  · Con neto: el precio no puede quedar por debajo del neto.
//  · Sin neto cargado: se permite el sugerido, pero no bajar de él.
export const errorPrecio = (valor, neto, sugerido) => {
  if (valor === '' || valor == null) return null;
  const v = Number(valor);
  if (!(v > 0)) return 'Escribe un precio mayor a 0.';
  const n = Number(neto);
  if (neto != null && neto !== '' && n > 0) return v < n - 0.005 ? 'Este precio está por debajo del costo.' : null;
  const s = Number(sugerido);
  if (s > 0 && v < s - 0.005) return 'Este producto no permite bajar del precio sugerido.';
  return null;
};

// ── PREMIUM (tiene_ficha) ──
export const armarPremium = ({ fila, idioma, tog, marca, precio, notasAsesor, producto }) => {
  const c = fila['contenido_' + idioma] || {};
  const ig = { ...(c.info_general || {}) };
  delete ig.cancelacion; // no se imprime cancelación
  ig.duracion = duracionDe(fila, ig);
  return {
    sku: fila.sku || producto.sku || '',
    idioma,
    agencia: marca,
    tour: {
      nombre: fila.nombre || producto.nombre,
      descripcion_corta: tog.descripcion ? (c.descripcion_corta || '') : '',
      highlights: tog.highlights ? arr(c.highlights) : [],
      incluye: arr(c.incluye),                     // siempre
      no_incluye: arr(c.no_incluye),               // siempre
      itinerario: tog.itinerario ? arr(c.itinerario) : [],
      info_general: ig,
      punto_encuentro: tog.punto ? arr(c.punto_encuentro) : [],
      notas: tog.antes ? arr(c.notas) : [],
      restricciones: tog.antes ? arr(c.restricciones) : [],
      faq: tog.faq ? arr(c.faq) : [],
      confianza: tog.confianza ? arr(c.confianza) : [],
    },
    imagenes: fotosDe(fila, tog.galeria),
    dias_operacion: tog.dias ? arr(fila.dias_operacion) : [],
    edades: tog.edades ? edadesDe(fila, idioma) : [],
    es_nacional: (fila.es_nacional ?? producto.es_nacional) === true,
    notasAgente: String(notasAsesor || '').trim(),
    precio,
  };
};

// ── BÁSICO (sin ficha: NIK, etc.; o temporal TMP-…) ──
export const armarBasico = ({ fila, tog, marca, precio, notasAsesor, producto }) => {
  const esTemporal = !fila;
  const c = (fila && fila.contenido_es) || {};
  const notas = sinCancel(esTemporal ? lineas(producto.notas) : [...arr(c.notas), ...lineas(fila.notas_biblia)].map(String));
  // Bloque 12 §1e: la tarifa nacional va como insignia INE (data.es_nacional), ya no como nota.
  const nacional = (fila ? fila.es_nacional : producto.es_nacional) === true;
  const ed = edadesDe(fila, 'es');
  const edad = (etq) => (ed.find((e) => e.etiqueta === etq) || {}).valor || '';
  return {
    agencia: marca,
    es_nacional: nacional,
    tour: {
      nombre: (fila && fila.nombre) || producto.nombre,
      descripcion: esTemporal ? '' : String(c.descripcion_corta || '').split(/(?<=[.!?])\s+/).filter((o) => !CANCELACION.test(o)).join(' '),
      duracion: fila ? duracionDe(fila, c.info_general) : '',
      dias: fila ? arr(fila.dias_operacion).map(String) : [],
      edadAdulto: edad('Adultos'), edadMenor: edad('Menores'), edadInfante: edad('Infantes'),
      incluye: esTemporal ? lineas(producto.incluye) : sinCancel(arr(c.incluye)),
      no_incluye: esTemporal ? [] : sinCancel(arr(c.no_incluye)),
      puntoEncuentro: fila ? arr(c.punto_encuentro) : [],
      notas,
      restricciones: fila ? sinCancel(arr(c.restricciones)) : [],
    },
    precio,
    toggles: { descripcion: tog.descripcion, edades: tog.edades, dias: tog.dias, puntoEncuentro: tog.punto, restricciones: tog.antes, notas: true },
    notasGlobales: String(notasAsesor || '').trim(),
  };
};

// ── TARJETA PROMO Top Tours (Bloque 13) ── una hoja estilo tarjetas de Maggie. Sin netos.
const DIAS_LARGO = { es: ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'], en: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'], pt: ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'] };
const TODOS = { es: 'Todos los días', en: 'Every day', pt: 'Todos os dias' };
const Y_ = { es: 'y', en: 'and', pt: 'e' };
export const diasLargo = (dias, idioma = 'es') => {
  const ns = [...new Set(arr(dias).map((d) => Number(d)).filter((n) => n >= 1 && n <= 7))].sort((a, b) => a - b);
  if (!ns.length) return '';
  if (ns.length === 7) return TODOS[idioma] || TODOS.es;
  const nom = ns.map((n) => (DIAS_LARGO[idioma] || DIAS_LARGO.es)[n - 1]);
  return nom.length === 1 ? nom[0] : `${nom.slice(0, -1).join(', ')} ${Y_[idioma] || 'y'} ${nom[nom.length - 1]}`;
};
// Recorta en palabra completa a `n` caracteres (sin "…" si cabe completo).
export const enPalabras = (s, n) => {
  const t = String(s || '').replace(/\s+/g, ' ').trim();
  if (t.length <= n) return t;
  const c = t.slice(0, n + 1);
  const e = c.lastIndexOf(' ');
  let r = (e > 0 ? c.slice(0, e) : t.slice(0, n)).replace(/[\s,;:.·-]+$/, '');
  // Sin palabras sueltas al final ("…EN COZUMEL CON" → "…EN COZUMEL").
  const COLGANTES = /\s+(con|de|del|la|las|el|los|y|e|en|a|al|para|por|un|una|with|and|the|of|to|in|com|da|do|na|no|o)$/i;
  while (COLGANTES.test(r)) r = r.replace(COLGANTES, '');
  return r;
};
const primeraFrase = (s) => String(s || '').split(/[.!?;]|,\s/)[0] || '';
const textoDe = (x) => (typeof x === 'string' ? x : x && (x.texto || x.text || x.titulo || x.title || x.nombre)) || '';
const NOTA_IMPORTANTE = /impuest|muelle|tax|derecho|brazalete|no incluye/i;

export const armarTarjeta = ({ fila, idioma, marca, precio, tasa, regular, lugares, producto, iconoDe }) => {
  const esFicha = !!(fila && fila.tiene_ficha);
  const c = (fila && (fila['contenido_' + idioma] || fila.contenido_es)) || {};
  const incluye = sinCancel((esFicha ? arr(c.incluye) : lineas(producto.incluye)).map(textoDe).filter(Boolean));
  const highlights = sinCancel(arr(c.highlights).map(textoDe).filter(Boolean));
  const notas = sinCancel([...arr(c.notas).map(textoDe), ...lineas(fila && fila.notas_biblia), ...lineas(producto.notas)].filter(Boolean));
  const fotos = fila ? arr(fila.galeria_urls).filter(Boolean).slice(0, 4).map((url, i) => ({ url, label: enPalabras(highlights[i] || '', 22).toUpperCase() })) : [];
  const t = Number(tasa) > 0 ? Number(tasa) : null;
  const m = Number(precio.monto) || 0, mm = Number(precio.montoMenor) || 0;
  const usd = precio.moneda === 'USD';
  const conv = (v) => (v > 0 ? { mxn: usd ? (t ? Math.round(v * t) : null) : v, usd: usd ? v : (t ? Math.round((v / t) * 100) / 100 : null) } : null);
  const promo = conv(m);
  const reg = regular && Number(regular) > m ? conv(Number(regular)) : null; // precio regular solo si es MAYOR que la promo
  return {
    idioma,
    moneda: usd ? 'USD' : 'MXN',
    marca,
    nombre: (fila && fila.nombre) || producto.nombre || '',
    subtitulo: enPalabras(primeraFrase(sinCancelTexto(c.descripcion_corta)), 32).toUpperCase(),
    portada: (fila && fila.portada_url) || null,
    iconos: incluye.slice(0, 7).map((x) => ({ icono: iconoDe(x), label: enPalabras(x, 32).toUpperCase() })),
    fotos,
    promo, menor: conv(mm), regular: reg,
    nota: notas.find((n) => NOTA_IMPORTANTE.test(n)) || '',
    diasTexto: diasLargo(fila ? fila.dias_operacion : [], idioma),
    lugares: Math.max(1, parseInt(lugares, 10) || 25),
    es_nacional: (fila ? fila.es_nacional : producto.es_nacional) === true,
  };
};
const sinCancelTexto = (s) => String(s || '').split(/(?<=[.!?])\s+/).filter((o) => !CANCELACION.test(o)).join(' ');

export const nombreArchivo = (nombre) => `INFO ${String(nombre || 'tour').replace(/[\\/:*?"<>|]/g, '').trim()}.pdf`;
